import { useCallback, useState } from "react";
import {
  createPost as createPostReq,
  deletePost as deletePostReq,
} from "../../services/postsService";
import { getCachedUserBundle, getCurrentUserId } from "../../services/authService";
import { trackEvent } from "../../services/trackingService";

// Owns the feed write-actions (create / delete / share) and the share-modal
// state, keeping Dashboard.js a layout shell. Optimistically updates the feed.
export function useFeedActions({
  profile,
  user,
  currentUserId,
  setCombinedPosts,
  toast,
}) {
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");

  // Create a post and optimistically prepend it. Throws on failure so the
  // caller can manage its own composer/posting UI state.
  const submitPost = useCallback(
    async ({ text, mediaFile, mediaType, tags }) => {
      const cachedBundle = getCachedUserBundle();
      const userId =
        getCurrentUserId() ||
        cachedBundle?.user?.id ||
        cachedBundle?.profile?.id ||
        profile?.user?.id ||
        profile?.id ||
        user?.id ||
        null;

      const { created, media_url, media_type } = await createPostReq({
        body: text,
        mediaFile,
        mediaType,
        userId,
        tags,
      });

      const firstTok = Array.isArray(tags) && tags[0] ? String(tags[0]) : "";
      const sep = firstTok.indexOf(":");
      const firstScope = sep > 0 ? firstTok.slice(0, sep) : null;
      const firstKey = sep > 0 ? firstTok.slice(sep + 1) : null;

      const postItem = {
        id: created.id || created.post_id || created.uuid || Date.now(),
        type: "post",
        user_id: userId || created.user_id || created.user?.id || null,
        body: created.body || created.text || (text || "").trim(),
        media_url:
          created.media_url ||
          created.media?.url ||
          created.image_url ||
          created.video_url ||
          media_url ||
          null,
        media_type:
          created.media_type ||
          created.media?.type ||
          (created.image_url ? "image" : created.video_url ? "video" : null) ||
          media_type ||
          null,
        created_at: created.created_at || new Date().toISOString(),
        author_first_name: created.author?.first_name || profile?.first_name || "",
        author_last_name: created.author?.last_name || profile?.last_name || "",
        author_avatar_url: created.author?.avatar_url || profile?.avatar_url || "",
        like_count: Number.isFinite(created.like_count)
          ? created.like_count
          : created.likes || 0,
        liked_by_me: !!created.liked_by_me,
        likers: Array.isArray(created.likers) ? created.likers : [],
        scope: created.scope || firstScope || null,
        team_key: created.team_key || firstKey || null,
        scopes: Array.isArray(tags) && tags.length ? tags : undefined,
      };

      setCombinedPosts((prev) => [postItem, ...prev]);
    },
    [profile, user, setCombinedPosts]
  );

  const removePost = useCallback(
    async (post) => {
      if (!post?.id) return;
      try {
        await deletePostReq(post.id, currentUserId);
        setCombinedPosts((prev) => prev.filter((item) => item.id !== post.id));
      } catch (e) {
        console.error("Delete post error:", e);
        toast({
          status: "error",
          title: "Cannot delete post",
          description: e.message || String(e),
        });
      }
    },
    [currentUserId, setCombinedPosts, toast]
  );

  const share = useCallback(
    (item) => {
      try {
        if (!item) return;
        const rawType = (item.type || "").toString().toLowerCase();
        let shareType = rawType || "post";
        let shareId =
          item.id || item.item_id || item.post_id || item.document_id || null;
        if (item.post_subtype === "uploaded_article") {
          shareType = "document";
          shareId =
            item.document?.id ||
            item.document_id ||
            item.id ||
            item.item_id ||
            item.post_id ||
            null;
        }
        if (!shareId || !shareType) {
          toast({
            status: "error",
            title: "Cannot share this item",
            description: "Missing type or id.",
          });
          return;
        }
        const origin =
          (typeof window !== "undefined" && window.location.origin) || "";
        const link = `${origin}/share?type=${encodeURIComponent(
          shareType
        )}&id=${encodeURIComponent(shareId)}`;
        setShareLink(link);
        setShareModalOpen(true);
        trackEvent("share", { targetType: shareType, targetId: String(shareId) });
      } catch (e) {
        toast({
          status: "error",
          title: "Cannot share this item",
          description: e.message || String(e),
        });
      }
    },
    [toast]
  );

  return {
    submitPost,
    removePost,
    share,
    shareModalOpen,
    setShareModalOpen,
    shareLink,
  };
}
